import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';

const Login = () => {
  const { isAuthenticated, signIn, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');

  // If already authenticated, redirect to workspace
  if (isAuthenticated) {
    return <Navigate to="/workspace" replace />;
  }

  const handleSignIn = async (e) => {
    e.preventDefault();
    console.log('📝 Form login submitted:', { email, password: password ? '***' : 'empty' });
    setIsLoggingIn(true);
    setError('');

    try {
      console.log('🔄 Calling signIn...');
      const result = await signIn(email, password);
      console.log('📊 SignIn result:', result);
      if (!result.success) {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError('Login failed. Please try again.');
    }

    setIsLoggingIn(false);
  };

  if (loading) {
    return (
      <div className="login-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <h2>🎵 DecodedMusic Analytics</h2>
        <p>Loading...</p>
        <div className="loading-spinner" style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 2s linear infinite'
        }}></div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Cognito Hosted UI configuration
  const domain = "https://auth.decodedmusic.com";
  const clientId = "354arlmd5u14k3td9qn9pfg54b";
  
  // Handle different environments
  const getRedirectUri = () => {
    const currentOrigin = window.location.origin;
    if (currentOrigin.includes('decodedmusic.com')) {
      return 'https://decodedmusic.com/callback';
    }
    return currentOrigin + '/callback';
  };
  
  const redirectUri = getRedirectUri();
  const loginUrl = `${domain}/login?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;

  const handleHostedUILogin = () => {
    console.log('🔐 Cognito Hosted UI Login clicked');
    console.log('Login URL:', loginUrl);
    console.log('Redirecting to Cognito...');
    window.location.href = loginUrl;
  };

  // Show login form
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: '1rem' }}>🎵 DecodedMusic</h1>
        <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Artist Login</h2>
        
        {/* Primary Login: Cognito Hosted UI */}
        <button
          onClick={handleHostedUILogin}
          style={{
            width: '100%',
            padding: '20px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginBottom: '1rem',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a67d8'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#667eea'}
        >
          🔐 Sign in with Cognito
        </button>



        <div style={{ 
          textAlign: 'center', 
          marginBottom: '2rem',
          color: '#718096',
          fontSize: '0.9rem'
        }}>
          <p>Secure authentication via AWS Cognito</p>
        </div>

        {/* Alternative: Direct Login Form */}
        <details style={{ marginTop: '1rem' }}>
          <summary style={{ 
            cursor: 'pointer', 
            color: '#667eea', 
            textAlign: 'center',
            fontSize: '0.9rem',
            marginBottom: '1rem'
          }}>
            Advanced: Direct Login Form
          </summary>
          
          <form onSubmit={handleSignIn}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoggingIn}
              style={{
                width: '100%',
                padding: '15px',
                marginBottom: '1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoggingIn}
              style={{
                width: '100%',
                padding: '15px',
                marginBottom: '1.5rem',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            
            {error && (
              <div style={{
                color: '#e53e3e',
                textAlign: 'center',
                marginBottom: '1rem',
                padding: '10px',
                backgroundColor: '#fed7d7',
                borderRadius: '5px'
              }}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoggingIn}
              style={{
                width: '100%',
                padding: '15px',
                backgroundColor: isLoggingIn ? '#a0aec0' : '#4a5568',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                marginBottom: '1rem'
              }}
            >
              {isLoggingIn ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

        </details>
        
        <div style={{ 
          textAlign: 'center', 
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f7fafc',
          borderRadius: '10px',
          fontSize: '0.8rem'
        }}>
          <strong>Artist ID Mapping:</strong><br />
          rdv@decodedmusic.com → rdv01 (Rue de Vivre)
        </div>
      </div>
    </div>
  );
};

export default Login;
